'use client';

import { AppError, AppErrorCode } from '@repo/error';
import { errorMessage, t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/alert-dialog';
import {
  createContext,
  type Dispatch,
  type HTMLAttributes,
  type MouseEvent,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link, type LinkProps, useNavigate } from 'react-router';

export interface UnsavedChangesModalContent {
  message?: string;
  dismissButtonLabel?: string;
  proceedLinkLabel?: string;
  proceedLinkHref?: string;
  proceedAction?: () => void;
}

export interface UnsavedChangesContext {
  modalContent: UnsavedChangesModalContent | undefined;
  setModalContent: Dispatch<SetStateAction<UnsavedChangesModalContent | undefined>>;
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
}

export const UnsavedChangesModal: React.FC<UnsavedChangesContext> = ({
  modalContent,
  setModalContent,
  showModal,
  setShowModal,
}) => {
  useLocale();
  return (
    <AlertDialog
      onOpenChange={() => {
        setShowModal(false);
      }}
      open={showModal}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('workspace.unsavedChanges')}</AlertDialogTitle>
          <AlertDialogDescription>{modalContent?.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              setShowModal(false);
            }}
          >
            {modalContent?.dismissButtonLabel ?? t('extra.dismiss')}
          </AlertDialogCancel>
          <AlertDialogAction>
            <Link
              onClick={() => {
                setShowModal(false);
                modalContent?.proceedAction?.();
                setModalContent(undefined);
              }}
              to={modalContent?.proceedLinkHref ?? '/'}
            >
              {modalContent?.proceedLinkLabel ?? t('extra.proceed')}
            </Link>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const UnsavedChangesContext = createContext<UnsavedChangesContext | undefined>(undefined);

export const UnsavedChangesProvider: React.FC<PropsWithChildren> = ({ children }) => {
  useLocale();

  const [modalContent, setModalContent] = useState<UnsavedChangesModalContent | undefined>(
    undefined,
  );
  const [showModal, setShowModal] = useState<boolean>(false);

  const context = useMemo(
    (): UnsavedChangesContext => ({
      modalContent,
      setModalContent,
      showModal,
      setShowModal,
    }),
    [getLocale(), modalContent, setModalContent, showModal, setShowModal],
  );

  return (
    <UnsavedChangesContext.Provider value={context}>
      {children}
      <UnsavedChangesModal {...context} />
    </UnsavedChangesContext.Provider>
  );
};

export function useSetUnsavedChanges() {
  const context = useContext(UnsavedChangesContext);

  if (context === undefined) {
    throw new AppError(AppErrorCode.UNKNOWN_ERROR, {
      message: 'useSetUnsavedChanges must be called within <UnsavedChangesProvider />',
    });
  }

  const { setModalContent } = context;

  const setUnsavedChanges = useCallback(
    (modalContent: Omit<UnsavedChangesModalContent, 'proceedLinkHref'>) => {
      setModalContent(modalContent);
    },
    [setModalContent],
  );

  const clearUnsavedChanges = useCallback(() => {
    setModalContent(undefined);
  }, [setModalContent]);

  return useMemo(
    () => ({
      setUnsavedChanges,
      clearUnsavedChanges,
    }),
    [getLocale(), setUnsavedChanges, clearUnsavedChanges],
  );
}

export function useUnsavedChanges() {
  const context = useContext(UnsavedChangesContext);

  if (context === undefined) {
    throw new AppError(AppErrorCode.UNKNOWN_ERROR, {
      message: 'useUnsavedChanges must be called within <UnsavedChangesProvider />',
    });
  }

  const { modalContent, setModalContent, setShowModal } = context;

  const showUnsavedChangesModal = useCallback(
    (proceedLinkHref: string) => {
      setModalContent((currentContent) => ({
        ...currentContent,
        proceedLinkHref,
      }));
      setShowModal(true);
    },
    [setModalContent, setShowModal],
  );

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (modalContent !== undefined) {
        e.preventDefault();
        e.returnValue = errorMessage(AppError.parse(modalContent));
        return;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [modalContent]);

  return useMemo(
    () => ({
      currentPageHasUnsavedChanges: modalContent !== undefined,
      showUnsavedChangesModal,
    }),
    [getLocale(), modalContent, showUnsavedChangesModal],
  );
}

export type CustomLinkProps = PropsWithChildren<LinkProps & HTMLAttributes<HTMLAnchorElement>>;

export const GuardLink: React.FC<LinkProps> = ({ to, onClick, children, ...linkProps }) => {
  useLocale();

  const nextRouter = useNavigate();
  const { currentPageHasUnsavedChanges, showUnsavedChangesModal } = useUnsavedChanges();

  const handleLinkClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();

      if (onClick) {
        onClick(e);
      }

      if (currentPageHasUnsavedChanges) {
        showUnsavedChangesModal(to.toString());
      } else {
        nextRouter(to.toString());
      }
    },
    [currentPageHasUnsavedChanges, to, nextRouter, onClick, showUnsavedChangesModal],
  );

  return (
    <Link onClick={handleLinkClick} to={to} {...linkProps}>
      {children}
    </Link>
  );
};
